/* eslint-disable @typescript-eslint/no-unused-vars */
import { RecaptchaLockedInfo } from '@/components/FormCreator/components/RecaptchaLockedInfo';
import { useCreateFieldModalParams } from '@/components/FormCreator/hooks/useCreateFieldModalParams';
import { EditorMediaManager } from '@/components/TemplateEditor/components/EditorMediaManager';
import { useCustomFormats } from '@/services/hooks/editor/useCustomFormats';
import { useModules } from '@/components/TemplateEditor/hooks/useModules';
import { useShowToasts } from '@/components/TemplateEditor/hooks/useShowToast';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { usePlanHaveRecaptchaFeature } from '@/services/hooks/usePlanHaveRecaptchaFeature';
import { useUpselling } from '@/services/hooks/useUpselling';
import { LockIcon } from '@/services/icons/LockedIcon';
import { isIntercomEnabled } from '@/services/Secuirty';
import { getGeneralInfo } from '@/store/account/actions';
import { getAccount } from '@/store/account/types';
import { getDialogStore, HideDialog, ShowDialog } from '@/store/dialog/actions';
import {
    getLandingPageCreatorStore,
    OpenUpsellingOverlay,
    SaveStart,
    SetIsTemplateOpen,
    SetTemplatePreview,
    TemplateChanged,
    TemplateFormDuplicated,
    TemplateFormPayloadUpdate,
    TemplateIncrementAutosaveCounter,
    TemplateSetMultiSignUpEditMessage,
    TemplateShowSaveErrors,
} from '@/store/landingPageCreator/actions';
import { getMediaManagerStore, ShowMediaManager } from '@/store/mediaManager/actions';
import { getEditorFilesStore, UploadEditorFile, UploadEditorFileSuccess } from '@/store/templateEditor/actions';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { ElasticTheme, EmojiPickerLocale, palette } from '@dev/ee-components';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { getStoredLanguage } from '@dev/ee-tools';
import {
    TemplateEditor as Editor,
    EditorBlockDuplicationDetails,
    EditorBlockEventType,
    EditorBlockSelectionDetails,
    EditorCreateSignUpFieldEventCallback,
    EditorExternalValidationError,
    EditorSaveData,
    EditorSaveError,
    EditorTemplateStatus,
    EditorTemplateVersion,
    EditorWebsiteModeOutputFormat,
    EditorWebsiteModeType,
    FileType,
} from '@dev/template-editor';
import { Portal } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';
import { useBrandColorsInTemplateEditor } from '@/services/hooks/useBrandColorsInTemplateEditor';
import { useTemplateEditorFormMergeFields } from '@/services/hooks/useTemplateEditorFormMergeFields';
import { EditorType } from '@/services/dictionaries/EditorType';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { TEMPLATE_EDITOR_NO_BLOCK_LIMIT } from '../../constants';
import { useMultiSignUpSettings } from '../../hooks/useMultiSignUpSettings';
import { useShowTemplateIsBeingSavedToast } from '../../hooks/useShowTemplateIsBeingSavedToast';
import { websiteStyles } from '../../tools/getWebsiteStyles';
import { UpsellingOverlay } from '../UpsellingOverlay';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${ElasticTheme.zIndex.modal};
    background: ${palette.snow[1]};

    .MuiContainer-root {
        padding-top: 60px;
    }
`;

const headerOptions = [];

// eslint-disable-next-line max-lines-per-function
export const TemplateEditorModal = (): JSX.Element => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { template, landingPageObject } = useSelector(getLandingPageCreatorStore);
    const dialog = useSelector(getDialogStore);
    const mediaManager = useSelector(getMediaManagerStore);
    const files = useSelector(getEditorFilesStore);
    const { fields } = useTemplateEditorFormMergeFields();
    const { PublicAccountID } = useSelector(getGeneralInfo);
    const [blocksValidationErrors, setBlocksValidationErrors] = useState<EditorExternalValidationError[]>();
    const { onModuleAdd, onModuleAddLocked, onModuleDelete, onModuleNameEdit } = useModules();
    const { showModulesLimitReachedToast } = useShowToasts();

    const selectedBlockIdRef = useRef<string | undefined>();
    const multiSignUpSettings = useMultiSignUpSettings({ selectedBlockIdRef });
    const templateRef = useRef(template);
    const { showTemplateIsBeingSavedToast } = useShowTemplateIsBeingSavedToast();
    const { boot: bootIntercom, shutdown: shutdownIntercom } = useIntercom() || {};
    const { brandColors, onBrandColorAdd, onBrandColorEdit, onBrandColorDelete } = useBrandColorsInTemplateEditor();    
    const [showImageTabOnly, setShowImageTabOnly] = useState(false);
    const [isLinkToFile, setIsLinkToFile] = useState(false);
    const [currentBlockId, setCurrentBlockId] = useState<string>();
    const [templateContent, setTemplateContent] = useState<EditorTemplateVersion>(null);
    const advancedOptions = useSelector(getAccount);
    const hasContactFeatures = advancedOptions?.AdvancedOptions?.EnableContactFeatures;
    const { isUpsellingVisible } = useUpselling(AccessLevel.ViewCustomFields);
    const historyBackAction = useHistoryBackActionContext();
    const { pushBackCallback, popBackCallback } = historyBackAction || {};
    const { getCreateFieldModalProps } = useCreateFieldModalParams({ popBackCallback });

    const isPlanHaveRecaptchaFeature = usePlanHaveRecaptchaFeature();
    const customFormats = useCustomFormats(EditorType.LandingPageCreator);
    const { linkInBio: { isLinkInBioInsteadLandingPageVisible } } = useDashboardElementVisibility();

    useEffect(() => {
        templateRef.current = template;
    }, [template]);

    useEffect(() => {
        shutdownIntercom();
        pushBackCallback(onBreadcrumbAction);
        document.documentElement.style.overflow = 'auto';
        return () => {
            isIntercomEnabled() && bootIntercom();
            bootIntercom();
            document.documentElement.style.overflow = null;
        };
    }, []);

    useEffect(() => {
        if (
            templateContent ||
            !template?.loadedJson ||
            ![EditorTemplateStatus.LOADED, EditorTemplateStatus.SAVED].includes(templateRef.current.status)
        ) {
            return;
        }
        setTemplateContent(template?.loadedJson);
    }, [template?.loadedJson]);

    const onTemplateChanged = () => {
        blocksValidationErrors && setBlocksValidationErrors(null);
        dispatch(TemplateChanged());
    };
    const getBlocksValidationErrors = (): EditorExternalValidationError[] => template.forms
    ?.filter((form) => !form.isDeleted && form.isError)
    .map(
        ({ blockClientId, isFirstTimeError }): EditorExternalValidationError => ({
            blockClientId,
            invalidSidebarTabsIndexes: [2],
            disableSidebarTabOpen: isFirstTimeError === false,
        })
    );
    const onSave = (data: EditorSaveData, exitOnSave: boolean, errors?: EditorSaveError[]): void => {
        const blocksValidationErrors = getBlocksValidationErrors();
        setBlocksValidationErrors(blocksValidationErrors);
        dispatch<any>(SaveStart({ data, exitOnSave, errors }))
            .then(() => {
                if (!exitOnSave || errors) {
                    return;
                }
                popBackCallback();
            })
            .catch(() => {
                console.log('LandingPage failed to save.');
            });
    };

    const onBreadcrumbAction = (): boolean => {
        if (
            templateRef.current.status === EditorTemplateStatus.SAVING ||
            templateRef.current.status === EditorTemplateStatus.MODIFIED
        ) {
            showTemplateIsBeingSavedToast();
            return false;
        }
        const blocksValidationErrors = getBlocksValidationErrors();
        setBlocksValidationErrors(blocksValidationErrors);
        if (blocksValidationErrors?.length || templateRef.current.status === EditorTemplateStatus.SAVE_ERROR) {
            dispatch(TemplateShowSaveErrors());
            return false;
        }
        dispatch(SetIsTemplateOpen(false));
        return true;
    };
    const onBreadcrumbClick = (): void => {
        onBreadcrumbAction() && popBackCallback();
    };

    const onPreviewTemplateOpen = (templateHTML: string) => {
        dispatch(SetTemplatePreview(templateHTML));
    };
    const closeModal = (): boolean => {
        dispatch(HideDialog());
        return true;
    };
    const onCreateSignUpField = (callback: EditorCreateSignUpFieldEventCallback) => {
        if (isUpsellingVisible) {
            dispatch(OpenUpsellingOverlay(AvailableFeaturesUpselling.CustomFields));
            return;
        }
        pushBackCallback(closeModal);
        dispatch(ShowDialog(getCreateFieldModalProps(callback)));
    };

    const openMediaManager = (blockId: string, isLinkToFile: boolean): void => {
        setCurrentBlockId(blockId);
        setShowImageTabOnly(!isLinkToFile);
        setIsLinkToFile(isLinkToFile);
        dispatch(ShowMediaManager());
    };

    const uploadFile = (file: File, blockId: string): void => {
        dispatch(UploadEditorFile(file, blockId));
    };

    const editorFilesUpdate = (files: FileType[]): void => {
        dispatch(UploadEditorFileSuccess(files));
    };

    const localeForEmoji = getStoredLanguage();

    const onRecaptchaUpselling = (): void => {
        dispatch(OpenUpsellingOverlay(AvailableFeaturesUpselling.Recaptcha));
    };

    const onBlockDuplication = (details: EditorBlockDuplicationDetails): void => {
        dispatch(TemplateFormDuplicated(details.block.clientId, details.clonedBlock.clientId));
    };

    const onBlockSelection = (details: EditorBlockSelectionDetails): void => {
        if (template.multiSignUpEditMessage && (!details.block?.clientId || selectedBlockIdRef.current !== details.block?.clientId)) {
            dispatch(TemplateSetMultiSignUpEditMessage());
        }
        selectedBlockIdRef.current = details.block?.clientId;
    };

    const onBlockEvent = (eventType: EditorBlockEventType, details): void => {
        if (template.multiSignUpEditMessage && eventType === EditorBlockEventType.SIDEBAR_TAB_SWITCH) {
            dispatch(TemplateSetMultiSignUpEditMessage());
        }
    };

    return (
        <Portal>
            <Overlay>
                <Editor
                    translations={
                        i18n.hasResourceBundle(i18n.language, 'template-editor')
                            ? i18n.getResourceBundle(i18n.language, 'template-editor')
                            : null
                    }
                    template={{
                        status: template?.status,
                        name: landingPageObject.Name,
                        content: templateContent,
                        isNameChangeDisabled: true,
                    }}
                    events={{
                        onFileUpload: uploadFile,
                        onMediaManagerOpen: openMediaManager,
                        onEditorFilesUpdate: editorFilesUpdate,
                        onTemplateChanged,
                        onSave,
                        onTemplatePreview: onPreviewTemplateOpen,
                        onBreadcrumbClick,
                        onBrandColorAdd,
                        onBrandColorDelete,
                        onBrandColorEdit,
                        onCreateSignUpField: hasContactFeatures ? onCreateSignUpField : null,
                        onModuleAdd,
                        onModuleDelete,
                        onModuleNameEdit,
                        onModuleLimitReached: showModulesLimitReachedToast,
                        onModuleAddLocked,
                        onBlockDuplication,
                        onBlockSelection,
                        onBlockEvent,
                    }}
                    media={{
                        mediaManagerComponent: {
                            content: (
                                <EditorMediaManager
                                    showImageTabOnly={showImageTabOnly}
                                    isLinkToFile={isLinkToFile}
                                    blockId={currentBlockId}
                                    isOpen={mediaManager.isOpened}
                                />
                            ),
                            isMediaManagerOpen: mediaManager.isOpened,
                        },
                        files,
                    }}
                    autosaveOptions={{
                        counter: template?.autosaveCounter,
                    }}
                    navigation={{
                        breadcrumbText: isLinkInBioInsteadLandingPageVisible ? t('backtolinkinbiopage') : t('backtolandingpage'),
                        isContinueButtonVariant: true,
                    }}
                    colorPickerOptions={{
                        recentColors: template?.recentColors,
                        brandColors,
                    }}
                    // modules={{
                    //     savedList,
                    //     savedLimit,
                    //     savedLocked: isSaveLocked,
                    //     savedUpgradeable: isSaveUpgradeable,
                    //     savedLockedInfoComponent: (
                    //         <SavedLockedInfo
                    //             button={{
                    //                 text: t('upgradetounlock'),
                    //                 startIcon: <LockIcon width={20} height={20} />,
                    //                 onClick: openModulesUpsellingOverlay,
                    //             }}
                    //         />
                    //     ),
                    //     prebuiltList: prebuiltModulesList,
                    //     nameMaxLength: 100,
                    //     savedModulesLimitInfo: <SavedModulesLimitInfo zIndex={1440} />,
                    // }}
                    emojiOptions={{
                        locale: localeForEmoji as EmojiPickerLocale,
                    }}
                    customFormats={customFormats}
                    websiteModeOptions={{
                        mode: EditorWebsiteModeType.FullWebsite,
                        output: {
                            format: EditorWebsiteModeOutputFormat.FullTemplate,
                        },
                        styles: websiteStyles,
                        rows: {
                            hide: false,
                        },
                        content: {
                            hide: false
                        },
                        signUpSettings: {
                            actionUrl: `${APIPATH}/v4/contact/form`,
                            target: '_self',
                            hiddenInputs: {
                                publicaccountid: PublicAccountID,
                            },
                            fields,
                            lists: [],
                            selectedLists: [],
                        },
                        multiSignUpSettings,
                        multiSignUpEditMessage: template?.multiSignUpEditMessage,
                        blocks: {
                            paragraph: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                allowTransformation: true,
                            },
                            heading: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                allowTransformation: true,
                            },
                            image: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            divider: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            social: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            list: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            formMulti: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            button: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            spacer: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            video: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                        },
                        components: {
                            recaptchaLockedInfoComponent: isPlanHaveRecaptchaFeature?.ViewAntiSpamUpsellingBanner ? (
                                <RecaptchaLockedInfo
                                    button={{
                                        text: t('upgradetounlock'),
                                        startIcon: <LockIcon width={20} height={20} />,
                                        onClick: onRecaptchaUpselling,
                                    }}
                                />
                            ) : null,
                        },
                        isRecaptchaHidden: !isPlanHaveRecaptchaFeature?.ViewSubscriptionAntiSpamFeature,
                    }}
                    headerOptions={headerOptions}
                    displayOptions={{
                        isBelowOtherLayer: dialog.isOpened,
                        popupsMinimalZIndex: 1400,
                    }}
                    blocksValidationErrors={blocksValidationErrors}
                />
            </Overlay>
            <UpsellingOverlay />
        </Portal>
    );
};
